import React, { useState, useRef, useEffect } from "react";
import "./auth.css";
import { useLocation, useNavigate } from "react-router-dom";
import Logo from "../../../assets/LogoNew2.png";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Collapse,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import PeopleIcon from "@mui/icons-material/People";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { UserAdd } from "../../../redux/reducer/CreateUser";
import { SendOTP, VerifyOTP } from "../../../redux/reducer/OTPReducer";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTheme } from "../../../context/ThemeContext";
import { backgroundPattern, userTypeColors } from "../../../config/themes";
import ThemePicker from "../../../components/ThemePicker";

// Validation Schemas
const emailSchema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  account_type: Yup.string().required("Please select account type"),
});

const passwordSchema = Yup.object().shape({
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Confirm password is required"),
});

const steps = ["Email", "Verify", "Password"];

const CreateUser = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { theme } = useTheme();

  // Get colors based on selected account type
  const getTypeColors = (type) => {
    return type === "Landlord"
      ? userTypeColors.landlord
      : userTypeColors.tenant;
  };

  const [activeStep, setActiveStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const [resendTimer, setResendTimer] = useState(0);
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [verifiedAccountType, setVerifiedAccountType] = useState("");
  const [alertMessage, setAlertMessage] = useState({ type: "", text: "" });

  const otpRefs = useRef([]);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  function useQuery() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
  }
  let query = useQuery();
  const hasEmail = query.get("email");
  console.log(location, "location");

  const emailFormik = useFormik({
    initialValues: {
      email: hasEmail || "",
      account_type: hasEmail ? "Tenant" : location?.state?.type || "Tenant",
    },
    validationSchema: emailSchema,
    onSubmit: async (values) => {
      setOtpSending(true);
      try {
        const res = await dispatch(
          SendOTP({ email: values.email, type: values.account_type })
        );
        if (res?.payload?.data?.success) {
          setAlertMessage({ type: "success", text: "OTP sent to your email!" });
          setVerifiedEmail(values.email);
          setVerifiedAccountType(values.account_type);
          setActiveStep(1);
          setResendTimer(60);
          setOtpValues(["", "", "", "", "", ""]);
        } else {
          setAlertMessage({
            type: "error",
            text: res?.payload?.data?.message || "Failed to send OTP",
          });
        }
      } catch (error) {
        setAlertMessage({
          type: "error",
          text: "Failed to send OTP. Please try again.",
        });
      }
      setOtpSending(false);
    },
  });

  const passwordFormik = useFormik({
    initialValues: { password: "", confirmPassword: "" },
    validationSchema: passwordSchema,
    onSubmit: async (values) => {
      setRegistering(true);
      try {
        const payload = {
          email: verifiedEmail,
          password: values.password,
          account_type:
            verifiedAccountType === "Landlord" ? "landlord" : "tenant",
        };
        const res = await dispatch(UserAdd({ values: payload }));
        if (res?.payload?.data?.message === "User Register Successfully") {
          setAlertMessage({
            type: "success",
            text: "Account created successfully! Redirecting to login...",
          });
          setTimeout(() => {
            navigate("/login", { state: { type: verifiedAccountType } });
          }, 1500);
        } else if (
          res?.payload?.data?.message?.includes("User Already Registered")
        ) {
          setAlertMessage({ type: "error", text: res?.payload?.data?.message });
        } else {
          setAlertMessage({
            type: "error",
            text: res?.payload?.data?.message || "Registration failed",
          });
        }
      } catch (error) {
        setAlertMessage({
          type: "error",
          text: "Registration failed. Please try again.",
        });
      }
      setRegistering(false);
    },
  });

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtpValues = [...otpValues];
    newOtpValues[index] = value.slice(-1);
    setOtpValues(newOtpValues);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);
    if (!/^\d+$/.test(pastedData)) return;
    const newOtpValues = [...otpValues];
    pastedData.split("").forEach((char, index) => {
      if (index < 6) newOtpValues[index] = char;
    });
    setOtpValues(newOtpValues);
    otpRefs.current[Math.min(pastedData.length - 1, 5)]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOTP = async () => {
    const otp = otpValues.join("");
    if (otp.length !== 6) {
      setAlertMessage({
        type: "error",
        text: "Please enter complete 6-digit OTP",
      });
      return;
    }
    setOtpVerifying(true);
    try {
      const res = await dispatch(VerifyOTP({ email: verifiedEmail, otp }));
      if (res?.payload?.data?.success) {
        setAlertMessage({
          type: "success",
          text: "Email verified successfully!",
        });
        setActiveStep(2);
      } else {
        setAlertMessage({
          type: "error",
          text: res?.payload?.data?.message || "Invalid OTP",
        });
      }
    } catch (error) {
      setAlertMessage({ type: "error", text: "OTP verification failed." });
    }
    setOtpVerifying(false);
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    setOtpSending(true);
    try {
      const res = await dispatch(
        SendOTP({ email: verifiedEmail, type: verifiedAccountType })
      );
      if (res?.payload?.data?.success) {
        setAlertMessage({ type: "success", text: "OTP resent!" });
        setResendTimer(60);
        setOtpValues(["", "", "", "", "", ""]);
      } else {
        setAlertMessage({
          type: "error",
          text: res?.payload?.data?.message || "Failed to resend OTP",
        });
      }
    } catch (error) {
      setAlertMessage({ type: "error", text: "Failed to resend OTP" });
    }
    setOtpSending(false);
  };

  const handleBack = () => {
    if (activeStep === 1) {
      setActiveStep(0);
      setOtpValues(["", "", "", "", "", ""]);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: theme.gradient,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        py: 4,
        transition: "background 0.5s ease",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: backgroundPattern,
        }}
      />

      <ThemePicker position="top-right" light={true} />

      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 4,
            bgcolor: "white",
            boxShadow: "0 25px 80px rgba(0,0,0,0.4)",
          }}
        >
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Box
              // component="img"
              // src={Logo}
              // alt="OTS Logo"
              sx={{
                height: 100,
                width: 100,
                mb: 1,
                display: "inline-block",
                backgroundColor: theme.primary,

                maskImage: `url(${Logo})`,
                WebkitMaskImage: `url(${Logo})`,
                maskRepeat: "no-repeat",
                WebkitMaskRepeat: "no-repeat",

                maskSize: "120%",
                WebkitMaskSize: "120%",

                maskPosition: "center",
                WebkitMaskPosition: "center",
              }}
              onClick={() => navigate("/")}
            />
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "#1a1a2e", mb: 1 }}
            >
              Create Account
            </Typography>
            {/* Dynamic Type Badge */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                bgcolor: theme.lightBg,
                // bgcolor: getTypeColors(
                //   activeStep === 0
                //     ? emailFormik.values.account_type
                //     : verifiedAccountType
                // ).lightBg,
                px: 2,
                py: 0.75,
                borderRadius: 2,
                transition: "all 0.3s ease",
              }}
            >
              {(activeStep === 0
                ? emailFormik.values.account_type
                : verifiedAccountType) === "Landlord" ? (
                <HomeWorkIcon
                  sx={{
                    color: theme.primary.primary,
                    fontSize: 20,
                    transition: "color 0.3s ease",
                  }}
                />
              ) : (
                <PeopleIcon
                  sx={{
                    color: theme.primary,
                    fontSize: 20,
                    transition: "color 0.3s ease",
                  }}
                />
              )}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: theme.primary,
                  transition: "color 0.3s ease",
                }}
              >
                {(activeStep === 0
                  ? emailFormik.values.account_type
                  : verifiedAccountType) === "Landlord"
                  ? "Property Owner"
                  : "Tenant"}{" "}
                Signup
              </Typography>
            </Box>
          </Box>

          <Box sx={{ width: "100%", mb: 3 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label, index) => {
                const currentTypeColors = getTypeColors(
                  activeStep === 0
                    ? emailFormik.values.account_type
                    : verifiedAccountType
                );
                return (
                  <Step key={label} completed={index < activeStep}>
                    <StepLabel
                      StepIconProps={{
                        sx: {
                          transition: "color 0.3s ease",
                          "&.Mui-active": { color: currentTypeColors.primary },
                          "&.Mui-completed": {
                            color: currentTypeColors.primary,
                          },
                        },
                      }}
                      sx={{
                        "& .MuiStepLabel-label": {
                          fontSize: "13px",
                          fontWeight: activeStep === index ? 600 : 400,
                          color:
                            activeStep === index
                              ? currentTypeColors.primary
                              : "#999",
                          transition: "color 0.3s ease",
                        },
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Box>

          {/* Alert Message */}
          <Collapse in={Boolean(alertMessage.text)}>
            <Alert
              severity={alertMessage.type || "info"}
              onClose={() => setAlertMessage({ type: "", text: "" })}
              sx={{ mb: 2, borderRadius: 2 }}
            >
              {alertMessage.text}
            </Alert>
          </Collapse>

          {/* Step 1: Email */}
          {activeStep === 0 && (
            <form onSubmit={emailFormik.handleSubmit}>
              <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                {/* Property Owner Card */}
                <Box
                  onClick={() =>
                    emailFormik.setFieldValue("account_type", "Landlord")
                  }
                  sx={{
                    flex: 1,
                    p: 2.5,
                    borderRadius: 3,
                    cursor: "pointer",
                    textAlign: "center",
                    border: "2px solid",
                    borderColor:
                      emailFormik.values.account_type === "Landlord"
                        ? userTypeColors.landlord.primary
                        : "#e0e0e0",
                    bgcolor:
                      emailFormik.values.account_type === "Landlord"
                        ? userTypeColors.landlord.lightBg
                        : "white",
                    transition: "all 0.3s ease",
                    transform:
                      emailFormik.values.account_type === "Landlord"
                        ? "scale(1.02)"
                        : "scale(1)",
                    boxShadow:
                      emailFormik.values.account_type === "Landlord"
                        ? `0 4px 20px ${userTypeColors.landlord.primary}30`
                        : "none",
                    "&:hover": {
                      borderColor: userTypeColors.landlord.primary,
                      bgcolor: userTypeColors.landlord.lightBg,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      mx: "auto",
                      mb: 1.5,
                      background:
                        emailFormik.values.account_type === "Landlord"
                          ? userTypeColors.landlord.gradient
                          : "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <HomeWorkIcon
                      sx={{
                        fontSize: 28,
                        color:
                          emailFormik.values.account_type === "Landlord"
                            ? "white"
                            : "#999",
                        transition: "color 0.3s ease",
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color:
                        emailFormik.values.account_type === "Landlord"
                          ? userTypeColors.landlord.primary
                          : "#666",
                      transition: "color 0.3s ease",
                    }}
                  >
                    Property Owner
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        emailFormik.values.account_type === "Landlord"
                          ? userTypeColors.landlord.primary
                          : "#999",
                      opacity: 0.8,
                    }}
                  >
                    List & manage properties
                  </Typography>
                </Box>

                {/* Tenant Card */}
                <Box
                  onClick={() =>
                    emailFormik.setFieldValue("account_type", "Tenant")
                  }
                  sx={{
                    flex: 1,
                    p: 2.5,
                    borderRadius: 3,
                    cursor: "pointer",
                    textAlign: "center",
                    border: "2px solid",
                    borderColor:
                      emailFormik.values.account_type === "Tenant"
                        ? userTypeColors.tenant.primary
                        : "#e0e0e0",
                    bgcolor:
                      emailFormik.values.account_type === "Tenant"
                        ? userTypeColors.tenant.lightBg
                        : "white",
                    transition: "all 0.3s ease",
                    transform:
                      emailFormik.values.account_type === "Tenant"
                        ? "scale(1.02)"
                        : "scale(1)",
                    boxShadow:
                      emailFormik.values.account_type === "Tenant"
                        ? `0 4px 20px ${userTypeColors.tenant.primary}30`
                        : "none",
                    "&:hover": {
                      borderColor: userTypeColors.tenant.primary,
                      bgcolor: userTypeColors.tenant.lightBg,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      mx: "auto",
                      mb: 1.5,
                      background:
                        emailFormik.values.account_type === "Tenant"
                          ? userTypeColors.tenant.gradient
                          : "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <PeopleIcon
                      sx={{
                        fontSize: 28,
                        color:
                          emailFormik.values.account_type === "Tenant"
                            ? "white"
                            : "#999",
                        transition: "color 0.3s ease",
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color:
                        emailFormik.values.account_type === "Tenant"
                          ? userTypeColors.tenant.primary
                          : "#666",
                      transition: "color 0.3s ease",
                    }}
                  >
                    Tenant
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        emailFormik.values.account_type === "Tenant"
                          ? userTypeColors.tenant.primary
                          : "#999",
                      opacity: 0.8,
                    }}
                  >
                    Find & rent properties
                  </Typography>
                </Box>
              </Box>

              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={emailFormik.values.email}
                onChange={emailFormik.handleChange}
                onBlur={emailFormik.handleBlur}
                disabled={Boolean(hasEmail)}
                error={
                  emailFormik.touched.email && Boolean(emailFormik.errors.email)
                }
                helperText={
                  emailFormik.touched.email && emailFormik.errors.email
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon
                        sx={{
                          color: getTypeColors(emailFormik.values.account_type)
                            .primary,
                          transition: "color 0.3s ease",
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2.5,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    transition: "all 0.3s ease",
                    "&:hover fieldset": {
                      borderColor: getTypeColors(
                        emailFormik.values.account_type
                      ).primary,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: getTypeColors(
                        emailFormik.values.account_type
                      ).primary,
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: getTypeColors(emailFormik.values.account_type)
                      .primary,
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={otpSending}
                sx={{
                  py: 1.5,
                  background: getTypeColors(emailFormik.values.account_type)
                    .gradient,
                  fontWeight: 600,
                  fontSize: "1rem",
                  borderRadius: 2,
                  textTransform: "none",
                  boxShadow: `0 4px 20px ${
                    getTypeColors(emailFormik.values.account_type).primary
                  }50`,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: getTypeColors(emailFormik.values.account_type)
                      .gradient,
                    boxShadow: `0 6px 25px ${
                      getTypeColors(emailFormik.values.account_type).primary
                    }70`,
                    transform: "translateY(-2px)",
                  },
                  "&:disabled": { bgcolor: "#ccc" },
                }}
              >
                {otpSending ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Send OTP"
                )}
              </Button>
            </form>
          )}

          {/* Step 2: OTP */}
          {activeStep === 1 && (
            <Box>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography variant="body2" sx={{ color: "#666", mb: 0.5 }}>
                  We've sent a 6-digit OTP to
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: getTypeColors(verifiedAccountType).primary,
                  }}
                >
                  {verifiedEmail}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 1.5,
                  mb: 3,
                }}
              >
                {otpValues.map((value, index) => (
                  <Box
                    key={index}
                    component="input"
                    ref={(el) => (otpRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={value}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={index === 0 ? handleOtpPaste : undefined}
                    autoFocus={index === 0}
                    sx={{
                      width: 48,
                      height: 56,
                      textAlign: "center",
                      fontSize: "1.5rem",
                      fontWeight: 600,
                      border: "2px solid",
                      borderColor: value
                        ? getTypeColors(verifiedAccountType).primary
                        : "#e0e0e0",
                      borderRadius: 2,
                      outline: "none",
                      transition: "all 0.2s ease",
                      "&:focus": {
                        borderColor: getTypeColors(verifiedAccountType).primary,
                        boxShadow: `0 0 0 3px ${
                          getTypeColors(verifiedAccountType).primary
                        }30`,
                      },
                    }}
                  />
                ))}
              </Box>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleVerifyOTP}
                disabled={otpVerifying || otpValues.join("").length !== 6}
                sx={{
                  py: 1.5,
                  background: getTypeColors(verifiedAccountType).gradient,
                  fontWeight: 600,
                  fontSize: "1rem",
                  borderRadius: 2,
                  textTransform: "none",
                  boxShadow: `0 4px 20px ${
                    getTypeColors(verifiedAccountType).primary
                  }50`,
                  "&:hover": {
                    background: getTypeColors(verifiedAccountType).gradient,
                    boxShadow: `0 6px 25px ${
                      getTypeColors(verifiedAccountType).primary
                    }70`,
                    transform: "translateY(-2px)",
                  },
                  "&:disabled": { bgcolor: "#ccc" },
                }}
              >
                {otpVerifying ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Verify OTP"
                )}
              </Button>

              <Box sx={{ textAlign: "center", mt: 3 }}>
                {resendTimer > 0 ? (
                  <Typography variant="body2" sx={{ color: "#999" }}>
                    Resend OTP in{" "}
                    <span
                      style={{
                        fontWeight: 600,
                        color: getTypeColors(verifiedAccountType).primary,
                      }}
                    >
                      {resendTimer}s
                    </span>
                  </Typography>
                ) : (
                  <Typography
                    variant="body2"
                    onClick={handleResendOTP}
                    sx={{
                      cursor: "pointer",
                      color: getTypeColors(verifiedAccountType).primary,
                      fontWeight: 600,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {otpSending ? "Sending..." : "Resend OTP"}
                  </Typography>
                )}
              </Box>

              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Button
                  variant="text"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                  sx={{
                    color: "#999",
                    textTransform: "none",
                    "&:hover": {
                      color: getTypeColors(verifiedAccountType).primary,
                    },
                  }}
                >
                  Change Email
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 3: Password */}
          {activeStep === 2 && (
            <form onSubmit={passwordFormik.handleSubmit}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  mb: 3,
                  p: 1.5,
                  bgcolor: getTypeColors(verifiedAccountType).lightBg,
                  borderRadius: 2,
                }}
              >
                <CheckCircleIcon
                  sx={{
                    color: getTypeColors(verifiedAccountType).primary,
                    fontSize: 22,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: getTypeColors(verifiedAccountType).primary,
                  }}
                >
                  {verifiedEmail}
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={passwordFormik.values.password}
                onChange={passwordFormik.handleChange}
                onBlur={passwordFormik.handleBlur}
                error={
                  passwordFormik.touched.password &&
                  Boolean(passwordFormik.errors.password)
                }
                helperText={
                  passwordFormik.touched.password &&
                  passwordFormik.errors.password
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon
                        sx={{
                          color: getTypeColors(verifiedAccountType).primary,
                        }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: getTypeColors(verifiedAccountType).primary,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: getTypeColors(verifiedAccountType).primary,
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: getTypeColors(verifiedAccountType).primary,
                  },
                }}
              />

              <TextField
                fullWidth
                label="Confirm Password"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={passwordFormik.values.confirmPassword}
                onChange={passwordFormik.handleChange}
                onBlur={passwordFormik.handleBlur}
                error={
                  passwordFormik.touched.confirmPassword &&
                  Boolean(passwordFormik.errors.confirmPassword)
                }
                helperText={
                  passwordFormik.touched.confirmPassword &&
                  passwordFormik.errors.confirmPassword
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon
                        sx={{
                          color: getTypeColors(verifiedAccountType).primary,
                        }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2.5,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: getTypeColors(verifiedAccountType).primary,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: getTypeColors(verifiedAccountType).primary,
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: getTypeColors(verifiedAccountType).primary,
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={registering}
                sx={{
                  py: 1.5,
                  background: getTypeColors(verifiedAccountType).gradient,
                  fontWeight: 600,
                  fontSize: "1rem",
                  borderRadius: 2,
                  textTransform: "none",
                  boxShadow: `0 4px 20px ${
                    getTypeColors(verifiedAccountType).primary
                  }50`,
                  "&:hover": {
                    background: getTypeColors(verifiedAccountType).gradient,
                    boxShadow: `0 6px 25px ${
                      getTypeColors(verifiedAccountType).primary
                    }70`,
                    transform: "translateY(-2px)",
                  },
                  "&:disabled": { bgcolor: "#ccc" },
                }}
              >
                {registering ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Create Account"
                )}
              </Button>
            </form>
          )}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "#666" }}>
              Already have an account?{" "}
              <Link
                to="/login"
                state={{
                  type:
                    activeStep === 0
                      ? emailFormik.values.account_type
                      : verifiedAccountType,
                }}
                style={{
                  color: getTypeColors(
                    activeStep === 0
                      ? emailFormik.values.account_type
                      : verifiedAccountType
                  ).primary,
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                Login
              </Link>
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center", mt: 2 }}>
            <Button
              variant="text"
              onClick={() => navigate("/")}
              sx={{
                color: "#999",
                textTransform: "none",
                "&:hover": {
                  color: getTypeColors(
                    activeStep === 0
                      ? emailFormik.values.account_type
                      : verifiedAccountType
                  ).primary,
                },
              }}
            >
              Back to Home
            </Button>
          </Box>
        </Paper>

        <Typography
          variant="body2"
          sx={{ textAlign: "center", mt: 3, color: "rgba(255,255,255,0.6)" }}
        >
          Owner Tenant Solution - Pakistan's #1 Rental Platform
        </Typography>
      </Container>
    </Box>
  );
};

export default CreateUser;
